import { CalendarQuarterDefault } from './calendarRangeDefaults';

/**
 * Pure helpers computing the calendar's fiscal-year window: which years, quarters and
 * months are shown, and where "today" falls inside those arrays.
 *
 * The fiscal year starts 1 December: the FY that contains today's date is identified by
 * its start year (`currentFYStartYear`), and the window spans `YEARS_BACK` fiscal years
 * before it through `YEARS_FORWARD` fiscal years after it (inclusive of the current FY).
 *
 * These are extracted from Calendar/index.tsx so they can be unit-tested against a frozen
 * clock without mounting the component.
 */

/** How many fiscal years before the current one the calendar shows. */
export const YEARS_BACK = 2;
/** How many fiscal years after the current one the calendar shows. */
export const YEARS_FORWARD = 1;

export type MonthData = {
  month: string;
  year: number;
  monthIndex: number;
};

export type QuarterData = CalendarQuarterDefault;

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

/** The fiscal year (Dec-start) that today's date falls in, expressed as its start year. */
function getCurrentFYStartYear(): number {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  return currentMonth === 11 ? currentYear : currentYear - 1;
}

/**
 * The fiscal quarter (1-4) and fiscal year (the calendar year the FY ends in) that today's
 * date falls in.
 */
export function getCurrentFiscalQuarterAndYear(): {
  fiscalQuarter: number;
  fiscalYear: number;
} {
  const today = new Date();
  const month = today.getMonth(); // 0 = Jan, 11 = Dec
  let fiscalQuarter, fiscalYear;
  if (month === 11 || month === 0 || month === 1) {
    fiscalQuarter = 1;
    fiscalYear = month === 11 ? today.getFullYear() + 1 : today.getFullYear();
  } else if (month >= 2 && month <= 4) {
    fiscalQuarter = 2;
    fiscalYear = today.getFullYear();
  } else if (month >= 5 && month <= 7) {
    fiscalQuarter = 3;
    fiscalYear = today.getFullYear();
  } else {
    fiscalQuarter = 4;
    fiscalYear = today.getFullYear();
  }
  return { fiscalQuarter, fiscalYear };
}

/**
 * Months for every fiscal year in the window (`YEARS_BACK` back through `YEARS_FORWARD`
 * forward), starting at December of the earliest fiscal year.
 */
export function getCurrentMonthsArray(): MonthData[] {
  const currentFYStartYear = getCurrentFYStartYear();
  const months: MonthData[] = [];

  for (let fyOffset = -YEARS_BACK; fyOffset <= YEARS_FORWARD; fyOffset++) {
    const fyStartYear = currentFYStartYear + fyOffset;

    // Each financial year: Dec of fyStartYear, then Jan-Nov of fyStartYear+1
    months.push({
      month: MONTH_NAMES[11], // Dec
      year: fyStartYear,
      monthIndex: 11
    });

    for (let monthIdx = 0; monthIdx <= 10; monthIdx++) {
      months.push({
        month: MONTH_NAMES[monthIdx],
        year: fyStartYear + 1,
        monthIndex: monthIdx
      });
    }
  }

  return months;
}

/**
 * Quarters for every fiscal year in the window (`YEARS_BACK` back through `YEARS_FORWARD`
 * forward).
 */
export function getCurrentQuartersArray(): QuarterData[] {
  const currentFYStartYear = getCurrentFYStartYear();
  const quarters: QuarterData[] = [];

  for (let fyOffset = -YEARS_BACK; fyOffset <= YEARS_FORWARD; fyOffset++) {
    const fyStartYear = currentFYStartYear + fyOffset;

    for (let q = 1; q <= 4; q++) {
      quarters.push({
        quarter: q,
        year: fyStartYear + 1 // Financial year is labeled by the calendar year it ends in
      });
    }
  }

  return quarters;
}

/** Today's position (index) within the array returned by {@link getCurrentMonthsArray}. */
export function getCurrentMonthPosition(): number {
  const currentMonthIndex = new Date().getMonth();
  const monthsBeforeCurrentFY = YEARS_BACK * 12;

  if (currentMonthIndex === 11) {
    // December is month 0 of the current FY.
    return monthsBeforeCurrentFY;
  }
  return monthsBeforeCurrentFY + currentMonthIndex + 1;
}

/** Today's position (index) within the array returned by {@link getCurrentQuartersArray}. */
export function getCurrentQuarterPosition(): number {
  const { fiscalQuarter, fiscalYear } = getCurrentFiscalQuarterAndYear();
  const currentFYStartYear = getCurrentFYStartYear();

  // Quarters are labeled with year = fyStartYear + 1, so the FY this quarter belongs to
  // started the calendar year before its label.
  const fyStartYearForQuarter = fiscalYear - 1;
  const fyOffset = fyStartYearForQuarter - currentFYStartYear;

  return (fyOffset + YEARS_BACK) * 4 + (fiscalQuarter - 1);
}
